import React from 'react';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptics';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'success';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  icon?: React.ReactNode;
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  icon,
  className,
  disabled,
  onClick,
  ...props
}: ButtonProps) {
  const baseStyles = 'font-bold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark active:scale-[0.96] shadow-lg';

  const variants = {
    primary: 'bg-gradient-gold text-dark hover:shadow-gold-lg focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed shadow-primary/20',
    secondary: 'bg-dark-card text-white border-2 border-gray-700/50 hover:border-primary/50 hover:bg-dark-lighter focus:ring-primary disabled:opacity-50 shadow-black/20',
    danger: 'bg-gradient-to-br from-error to-red-700 text-white hover:shadow-lg hover:shadow-error/30 focus:ring-error disabled:opacity-50 shadow-error/20',
    ghost: 'bg-transparent text-gray-300 hover:bg-dark-lighter focus:ring-primary shadow-none',
    success: 'bg-gradient-to-br from-success to-green-700 text-white hover:shadow-lg hover:shadow-success/30 focus:ring-success disabled:opacity-50 shadow-success/20',
  };

  const sizes = {
    sm: 'px-4 py-2 text-sm min-h-[36px]',
    md: 'px-5 py-3 text-base min-h-[48px]',
    lg: 'px-8 py-4 text-lg min-h-[56px]',
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && !isLoading) {
      haptic.light();
      onClick?.(e);
    }
  };

  return (
    <button
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      disabled={disabled || isLoading}
      onClick={handleClick}
      {...props}
    >
      {isLoading ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Processing...</span>
        </span>
      ) : (
        <span className="flex items-center justify-center gap-2">
          {icon}
          {children}
        </span>
      )}
    </button>
  );
}
